import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { CLIENT_HEADER, clientKind } from "./auth/client";
import { decodeClaims, tokenManager } from "./auth/token-manager";

declare module "axios" {
  interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    authRetried?: boolean;
  }
}

export const request = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
  headers: { [CLIENT_HEADER]: clientKind },
});

export const setRequestAccessToken = (token: string | null) => {
  request.defaults.headers.common["Authorization"] = token
    ? `Bearer ${token}`
    : "";
};

tokenManager.subscribe(() =>
  setRequestAccessToken(tokenManager.getAccessToken()),
);

request.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (config.skipAuthRefresh) {
    return config;
  }
  const token = await tokenManager.getValidAccessToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  } else if (tokenManager.isSignedIn()) {
    // Signed in, but the token could not be renewed right now (offline or
    // rate limited). Fail like a network error instead of sending the call
    // without credentials and reading the 401 as a sign-out.
    throw new AxiosError(
      "Your session is reconnecting. Please try again in a moment.",
      "ERR_SESSION_UNAVAILABLE",
      config,
    );
  }
  return config;
});

const sessionOf = (config: InternalAxiosRequestConfig) => {
  const header = String(config.headers?.get?.("Authorization") ?? "");
  return header.startsWith("Bearer ")
    ? decodeClaims(header.slice("Bearer ".length)).sid
    : undefined;
};

// An expired access token is renewed once and the request replayed; a session
// that was signed out elsewhere ends here. Other 401s (a provider refusing a
// connected account, say) are not about the UniDeck session at all.
request.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config;
  const code = (error.response?.data as { code?: string } | undefined)?.code;

  if (
    error.response?.status === 401 &&
    config &&
    !config.skipAuthRefresh &&
    tokenManager.isSignedIn()
  ) {
    if (code === "SESSION_REVOKED" || code === "SESSION_EXPIRED") {
      // A late answer for an older session must not end the current one.
      const failed = sessionOf(config);
      if (!failed || failed === tokenManager.getState().sessionKey) {
        await tokenManager.clear();
      }
    } else if (!config.authRetried && code?.startsWith("TOKEN_")) {
      config.authRetried = true;
      if (await tokenManager.refresh()) {
        config.headers.set(
          "Authorization",
          `Bearer ${tokenManager.getAccessToken()}`,
        );
        return request(config);
      }
    }
  }

  throw error;
});
